import type {
  RfqAttachment,
  RfqAward,
  RfqCategory,
  RfqLine,
  RfqPriceMode,
  RfqQuote,
  RfqReplyStatus,
  RfqStatus,
  RfqVendor,
} from "./types";

import { allOrThrow, single } from "@/lib/orpc-batch";
import {
  normalizeCategoryId,
  normalizeSubType,
  parseSpec,
} from "@/pages/ItemEditor/itemMapping";
import { orpcClient } from "@/lib/orpc-client";

export type RfqServerStatus = "inquiring" | "underReview" | "completed";

/** 側邊欄各生命週期的列表頁：草稿 (inquiring) → 審核中 → 已完成 */
export const RFQ_LIST_PATHS: Record<RfqServerStatus, string> = {
  inquiring: "/hrMainPage/rfqDraft",
  underReview: "/hrMainPage/rfqUnderReview",
  completed: "/hrMainPage/rfqCompleted",
};

export function rfqListPath(status?: string): string {
  return RFQ_LIST_PATHS[status as RfqServerStatus] ?? RFQ_LIST_PATHS.inquiring;
}
export type RfqServerPricingMode = "byWeight" | "byUnit";
export type RfqServerQuoteStatus = "awaitingQuote" | "submitted" | "declined";

const STATUS_TO_VIEW: Record<RfqServerStatus, RfqStatus> = {
  inquiring: "inquiring",
  underReview: "pendingReview",
  completed: "awarded",
};

const REPLY_TO_VIEW: Record<RfqServerQuoteStatus, RfqReplyStatus> = {
  awaitingQuote: "pending",
  submitted: "quoted",
  declined: "declined",
};

const CATEGORY_BY_ITEM_CATEGORY: Record<string, RfqCategory> = {
  steel: "material",
  screw: "bolt",
  parts: "other",
  consumables: "other",
  hardware: "other",
};

export function toViewStatus(status: string): RfqStatus {
  return STATUS_TO_VIEW[status as RfqServerStatus] ?? "inquiring";
}

export function toViewPriceMode(mode: string): RfqPriceMode {
  return mode === "byWeight" ? "weight" : "unit";
}

export function toServerPricingMode(mode: RfqPriceMode): RfqServerPricingMode {
  return mode === "weight" ? "byWeight" : "byUnit";
}

export function toDateInput(value: Date | string | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function toDateTimeLabel(value: Date | string | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${toDateInput(date)} ${hours}:${minutes}`;
}

export interface RfqHeader {
  id: string;
  code: string;
  name: string;
  issueDate: string;
  deliveryLocation: string;
  paymentTerm: string;
  priceBasis: string;
  status: RfqStatus;
  serverStatus: RfqServerStatus;
  remark: string;
  sourceOrderIds: string[];
  sourceOrderLabels: string[];
  applicantId: string;
  applicantName: string;
  reviewerId: string | null;
  reviewerName: string;
  reviewDate: string;
  attachments: RfqAttachment[];
}

export interface RfqSummary {
  id: string;
  code: string;
  name: string;
  issueDate: string;
  deliveryLocation: string;
  status: RfqStatus;
  serverStatus: RfqServerStatus;
  applicantName: string;
}

export interface RfqHistoryRow {
  id: string;
  action: string;
  operatorId: string | null;
  rfqQuoteId: string | null;
  occurredAt: Date | string;
  detail: Record<string, unknown> | null;
}

export interface RfqProductOption {
  id: string;
  name: string;
  partnerId: string;
  unit: string;
  material: string;
  spec: string;
  length: number | null;
  unitWeight: number | null;
  category: RfqCategory;
  sectionType: string;
}

export interface RfqPartnerOption {
  id: string;
  name: string;
  taxId: string;
  contact: string;
  phone: string;
  email: string;
}

export interface RfqDetailData {
  header: RfqHeader;
  lines: RfqLine[];
  vendors: RfqVendor[];
  quotes: RfqQuote[];
  awards: RfqAward[];
  historyRows: RfqHistoryRow[];
  employeeNames: Record<string, string>;
  products: RfqProductOption[];
}

interface ServerRfq {
  id: string;
  code: string;
  name: string;
  issueDate: Date | string;
  deliveryLocation: string;
  paymentTerm: string;
  priceBasis: string;
  status: string;
  reviewerId: string | null;
  reviewDate: Date | string | null;
  applicantId: string;
  remark: string | null;
  sourceOrderIds: string[] | null;
  attachment: RfqAttachment[] | null;
  rfqItem?: ServerRfqItem[];
  rfqQuote?: ServerRfqQuote[];
  rfqAward?: ServerRfqAward[];
}

interface ServerRfqItem {
  id: string;
  rfqId: string;
  productId: string;
  seq: number;
  quantity: number;
  pricingMode: string;
  requiredDeliveryDate: Date | string;
  materialCert: boolean | null;
  remark: string | null;
  targetUnitPrice: number | null;
}

interface ServerRfqQuoteItem {
  id: string;
  rfqQuoteId: string;
  rfqItemId: string;
  isAvailable: boolean;
  unitPrice: number | null;
  includesFreight: boolean | null;
  includesTax: boolean | null;
  deliveryDate: Date | string | null;
  remark: string | null;
}

interface ServerRfqQuote {
  id: string;
  rfqId: string;
  partnerId: string;
  status: string;
  invitedAt: Date | string;
  rfqQuoteItem?: ServerRfqQuoteItem[];
}

interface ServerRfqAward {
  id: string;
  rfqId: string;
  rfqItemId: string;
  awardedQuoteId: string | null;
  notPurchased: boolean;
}

interface ServerProduct {
  id: string;
  name: string;
  partnerId: string;
  itemId: string | null;
  defaultItemId: string | null;
  unit: string | null;
  material: string | null;
  length: number | null;
  unitWeight: number | null;
}

interface ServerItem {
  id: string;
  mainCategory: string | null;
  subCategory: string | null;
  sectionDimension: unknown;
  name: string;
  description: string | null;
}

interface ServerPartner {
  id: string;
  name: string;
  businessIdNumber: string;
  telephone: string | null;
  contacts: { name?: string; telephone?: string; email?: string }[] | null;
  status: string;
}

interface ServerEmployee {
  id: string;
  name: string;
}

function buildProductOption(
  product: ServerProduct,
  item: ServerItem | undefined,
): RfqProductOption {
  const categoryId = item ? normalizeCategoryId(item.mainCategory) : "";
  const steelType = item ? normalizeSubType(item.subCategory) : undefined;
  const spec = item ? parseSpec(item.sectionDimension, steelType) : undefined;
  return {
    id: product.id,
    name: product.name,
    partnerId: product.partnerId,
    unit: product.unit ?? "",
    material: product.material ?? "",
    spec: spec ?? "",
    length: product.length,
    unitWeight: product.unitWeight,
    category: CATEGORY_BY_ITEM_CATEGORY[categoryId] ?? "other",
    sectionType: steelType ?? "",
  };
}

function describeSpec(option: RfqProductOption | undefined): string {
  if (!option) return "";
  const parts: string[] = [];
  if (option.spec) parts.push(option.spec);
  if (option.material) parts.push(option.material);
  if (option.length) parts.push(`L=${option.length}`);
  return parts.join(" ・ ");
}

export function toRfqLine(
  item: ServerRfqItem,
  option: RfqProductOption | undefined,
): RfqLine {
  return {
    id: item.id,
    productId: item.productId,
    seq: item.seq,
    category: option?.category ?? "other",
    name: option?.name ?? item.productId,
    spec: describeSpec(option),
    qty: item.quantity,
    unit: option?.unit || "式",
    priceMode: toViewPriceMode(item.pricingMode),
    sourceType: "manual",
    dueDate: toDateInput(item.requiredDeliveryDate),
    mtc: item.materialCert ?? false,
    targetPrice: item.targetUnitPrice ?? undefined,
    note: item.remark ?? undefined,
    material: option?.material || undefined,
    unitWeight: option?.unitWeight ?? undefined,
    sectionType: option?.sectionType || undefined,
    nominalSpec: option?.spec || undefined,
    lengthM: option?.length ?? undefined,
    count: item.quantity,
  };
}

function toRfqVendor(
  quote: ServerRfqQuote,
  partner: ServerPartner | undefined,
): RfqVendor {
  const contact = partner?.contacts?.[0];
  return {
    id: quote.id,
    partnerId: quote.partnerId,
    name: partner?.name ?? quote.partnerId,
    taxId: partner?.businessIdNumber ?? "",
    contact: contact?.name ?? "",
    phone: contact?.telephone ?? partner?.telephone ?? "",
    email: contact?.email ?? "",
    replyStatus: REPLY_TO_VIEW[quote.status as RfqServerQuoteStatus] ?? "pending",
  };
}

function toRfqQuote(
  quoteItem: ServerRfqQuoteItem,
  line: RfqLine | undefined,
  previousUnitPrice: number | undefined,
): RfqQuote {
  return {
    id: quoteItem.id,
    vendorId: quoteItem.rfqQuoteId,
    lineId: quoteItem.rfqItemId,
    round: previousUnitPrice != null ? 2 : 1,
    supplied: quoteItem.isAvailable && quoteItem.unitPrice != null,
    quoteQty: line?.qty ?? 0,
    quoteUnit: line?.unit ?? "",
    unitPrice: quoteItem.unitPrice ?? 0,
    taxIncluded: quoteItem.includesTax ?? false,
    freightIncluded: quoteItem.includesFreight ?? false,
    deliveryDate: toDateInput(quoteItem.deliveryDate) || undefined,
    remark: quoteItem.remark ?? undefined,
  };
}

function toPreviousQuote(quote: RfqQuote, previousUnitPrice: number): RfqQuote {
  return {
    ...quote,
    id: undefined,
    round: 1,
    unitPrice: previousUnitPrice,
    supplied: true,
  };
}

export async function loadProductCatalog(): Promise<RfqProductOption[]> {
  const [products, items, defaultItems] = await Promise.all([
    orpcClient.product.list({}) as Promise<unknown>,
    orpcClient.item.list({}) as Promise<unknown>,
    orpcClient.defaultItem.list({}) as Promise<unknown>,
  ]);
  const itemMap = new Map<string, ServerItem>();
  for (const row of (items as ServerItem[]) ?? []) itemMap.set(row.id, row);
  for (const row of (defaultItems as ServerItem[]) ?? []) itemMap.set(row.id, row);

  return ((products as ServerProduct[]) ?? []).map((product) =>
    buildProductOption(
      product,
      itemMap.get(product.itemId ?? product.defaultItemId ?? ""),
    ),
  );
}

export async function loadPartnerOptions(): Promise<RfqPartnerOption[]> {
  const partners = (await orpcClient.partner.list({})) as unknown as ServerPartner[];
  return (partners ?? [])
    .filter((partner) => partner.status !== "disabled")
    .map((partner) => {
      const contact = partner.contacts?.[0];
      return {
        id: partner.id,
        name: partner.name,
        taxId: partner.businessIdNumber ?? "",
        contact: contact?.name ?? "",
        phone: contact?.telephone ?? partner.telephone ?? "",
        email: contact?.email ?? "",
      };
    });
}

async function loadEmployeeNames(): Promise<Record<string, string>> {
  const employees = (await orpcClient.employee.list({})) as unknown as ServerEmployee[];
  const map: Record<string, string> = {};
  for (const employee of employees ?? []) map[employee.id] = employee.name;
  return map;
}

export async function listRfqSummaries(
  status?: RfqServerStatus,
): Promise<RfqSummary[]> {
  const [rows, employeeNames] = await Promise.all([
    orpcClient.rfq.list(
      status ? { filter: { status } } : {},
    ) as unknown as Promise<ServerRfq[]>,
    loadEmployeeNames(),
  ]);
  return (rows ?? []).map((row) => ({
    id: row.id,
    code: row.code,
    name: row.name,
    issueDate: toDateInput(row.issueDate),
    deliveryLocation: row.deliveryLocation,
    status: toViewStatus(row.status),
    serverStatus: row.status as RfqServerStatus,
    applicantName: employeeNames[row.applicantId] ?? "",
  }));
}

export async function loadRfqDetail(id: string): Promise<RfqDetailData> {
  const [detail, products, partners, employeeNames, historyRows] =
    await Promise.all([
      single(orpcClient.rfq.read([{ id }])) as unknown as Promise<ServerRfq>,
      loadProductCatalog(),
      orpcClient.partner.list({}) as unknown as Promise<ServerPartner[]>,
      loadEmployeeNames(),
      orpcClient.rfq.history.list({ rfqId: id }) as unknown as Promise<
        RfqHistoryRow[]
      >,
    ]);

  if (!detail) throw new Error("rfq not found");

  const productMap = new Map<string, RfqProductOption>();
  for (const option of products) productMap.set(option.id, option);

  const partnerMap = new Map<string, ServerPartner>();
  for (const partner of partners ?? []) partnerMap.set(partner.id, partner);

  const serverItems = [...(detail.rfqItem ?? [])].sort((a, b) => a.seq - b.seq);
  const lines = serverItems.map((item) =>
    toRfqLine(item, productMap.get(item.productId)),
  );
  const lineMap = new Map<string, RfqLine>();
  for (const line of lines) lineMap.set(line.id, line);

  const serverQuotes = detail.rfqQuote ?? [];
  const vendors = serverQuotes.map((quote) =>
    toRfqVendor(quote, partnerMap.get(quote.partnerId)),
  );

  const previousPrices = new Map<string, number>();
  for (const row of historyRows ?? []) {
    if (row.action !== "proxyNegotiationRecorded") continue;
    const rfqItemId = row.detail?.rfqItemId;
    const previous = row.detail?.previousUnitPrice;
    if (typeof rfqItemId !== "string" || typeof previous !== "number") continue;
    const key = `${row.rfqQuoteId ?? ""}|${rfqItemId}`;
    if (!previousPrices.has(key)) previousPrices.set(key, previous);
  }

  const quotes: RfqQuote[] = [];
  for (const serverQuote of serverQuotes) {
    for (const quoteItem of serverQuote.rfqQuoteItem ?? []) {
      const line = lineMap.get(quoteItem.rfqItemId);
      const previous = previousPrices.get(
        `${serverQuote.id}|${quoteItem.rfqItemId}`,
      );
      const current = toRfqQuote(quoteItem, line, previous);
      if (previous != null) quotes.push(toPreviousQuote(current, previous));
      quotes.push(current);
    }
  }

  const awards: RfqAward[] = lines.map((line) => {
    const award = (detail.rfqAward ?? []).find(
      (row) => row.rfqItemId === line.id,
    );
    return {
      lineId: line.id,
      vendorId: award?.awardedQuoteId ?? null,
      qty: line.qty,
      skip: award?.notPurchased ?? false,
    };
  });

  const sourceOrderIds = detail.sourceOrderIds ?? [];
  let sourceOrderLabels = sourceOrderIds;
  if (sourceOrderIds.length > 0) {
    try {
      const orders = await loadPurchaseOrderSources();
      const orderMap = new Map<string, string>();
      for (const order of orders) orderMap.set(order.id, order.name);
      sourceOrderLabels = sourceOrderIds.map(
        (orderId) => orderMap.get(orderId) ?? orderId,
      );
    } catch {
      sourceOrderLabels = sourceOrderIds;
    }
  }

  const header: RfqHeader = {
    id: detail.id,
    code: detail.code,
    name: detail.name,
    issueDate: toDateInput(detail.issueDate),
    deliveryLocation: detail.deliveryLocation,
    paymentTerm: detail.paymentTerm,
    priceBasis: detail.priceBasis,
    status: toViewStatus(detail.status),
    serverStatus: detail.status as RfqServerStatus,
    remark: detail.remark ?? "",
    sourceOrderIds,
    sourceOrderLabels,
    applicantId: detail.applicantId,
    applicantName: employeeNames[detail.applicantId] ?? "",
    reviewerId: detail.reviewerId,
    reviewerName: detail.reviewerId
      ? (employeeNames[detail.reviewerId] ?? "")
      : "",
    reviewDate: toDateInput(detail.reviewDate),
    attachments: Array.isArray(detail.attachment) ? detail.attachment : [],
  };

  return {
    header,
    lines,
    vendors,
    quotes,
    awards,
    historyRows: historyRows ?? [],
    employeeNames,
    products,
  };
}

export interface RfqHeaderInput {
  name: string;
  issueDate: string;
  deliveryLocation: string;
  paymentTerm: string;
  priceBasis: string;
  remark?: string;
  sourceOrderIds?: string[];
  attachments?: RfqAttachment[];
}

export async function createRfq(input: RfqHeaderInput): Promise<{ id: string }> {
  return (await single(
    orpcClient.rfq.create([
      {
        name: input.name,
        issueDate: new Date(input.issueDate),
        deliveryLocation: input.deliveryLocation,
        paymentTerm: input.paymentTerm,
        priceBasis: input.priceBasis,
        remark: input.remark || null,
        sourceOrderIds: input.sourceOrderIds ?? null,
        attachment: input.attachments ?? null,
      },
    ] as any),
  )) as unknown as { id: string };
}

export async function updateRfqHeader(
  id: string,
  input: Partial<RfqHeaderInput>,
): Promise<void> {
  const payload: Record<string, unknown> = { id };
  if (input.name !== undefined) payload.name = input.name;
  if (input.issueDate !== undefined) payload.issueDate = new Date(input.issueDate);
  if (input.deliveryLocation !== undefined)
    payload.deliveryLocation = input.deliveryLocation;
  if (input.paymentTerm !== undefined) payload.paymentTerm = input.paymentTerm;
  if (input.priceBasis !== undefined) payload.priceBasis = input.priceBasis;
  if (input.remark !== undefined) payload.remark = input.remark || null;
  if (input.sourceOrderIds !== undefined)
    payload.sourceOrderIds = input.sourceOrderIds;
  if (input.attachments !== undefined) payload.attachment = input.attachments;
  await single(orpcClient.rfq.update([payload] as any));
}

export async function deleteRfq(id: string): Promise<void> {
  await single(orpcClient.rfq.delete([{ id }]));
}

export interface RfqLineInput {
  productId: string;
  seq: number;
  quantity: number;
  priceMode: RfqPriceMode;
  requiredDeliveryDate: string;
  materialCert?: boolean;
  remark?: string;
  targetUnitPrice?: number | null;
}

export async function createRfqLines(
  rfqId: string,
  inputs: RfqLineInput[],
): Promise<void> {
  if (inputs.length === 0) return;
  await allOrThrow(
    orpcClient.rfq.item.create(
      inputs.map((input) => ({
        rfqId,
        productId: input.productId,
        seq: input.seq,
        quantity: input.quantity,
        pricingMode: toServerPricingMode(input.priceMode),
        requiredDeliveryDate: new Date(input.requiredDeliveryDate),
        materialCert: input.materialCert ?? false,
        remark: input.remark || null,
        targetUnitPrice: input.targetUnitPrice ?? null,
      })) as any,
    ),
  );
}

export async function updateRfqLine(
  id: string,
  patch: Partial<RfqLine>,
): Promise<void> {
  const payload: Record<string, unknown> = { id };
  if (patch.seq !== undefined) payload.seq = patch.seq;
  if (patch.qty !== undefined) payload.quantity = patch.qty;
  if (patch.priceMode !== undefined)
    payload.pricingMode = toServerPricingMode(patch.priceMode);
  if (patch.dueDate !== undefined && patch.dueDate)
    payload.requiredDeliveryDate = new Date(patch.dueDate);
  if (patch.mtc !== undefined) payload.materialCert = patch.mtc;
  if (patch.note !== undefined) payload.remark = patch.note || null;
  if (patch.targetPrice !== undefined)
    payload.targetUnitPrice = patch.targetPrice ?? null;
  await single(orpcClient.rfq.item.update([payload] as any));
}

export async function deleteRfqLines(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  await allOrThrow(orpcClient.rfq.item.delete(ids.map((id) => ({ id }))));
}

export async function addRfqVendors(
  rfqId: string,
  partnerIds: string[],
): Promise<void> {
  if (partnerIds.length === 0) return;
  await allOrThrow(
    orpcClient.rfq.quote.create(
      partnerIds.map((partnerId) => ({ rfqId, partnerId })) as any,
    ),
  );
}

export async function setVendorStatus(
  vendorId: string,
  status: RfqServerQuoteStatus,
): Promise<void> {
  await single(orpcClient.rfq.quote.update([{ id: vendorId, status }] as any));
}

export async function removeRfqVendor(vendorId: string): Promise<void> {
  await single(orpcClient.rfq.quote.delete([{ id: vendorId }]));
}

export interface QuoteItemInput {
  quoteItemId?: string;
  rfqItemId: string;
  isAvailable: boolean;
  unitPrice: number | null;
  includesFreight: boolean;
  includesTax: boolean;
  deliveryDate?: string;
  remark?: string;
}

export async function saveVendorQuotes(
  vendorId: string,
  rows: QuoteItemInput[],
): Promise<void> {
  const creates = rows.filter((row) => !row.quoteItemId);
  const updates = rows.filter((row) => !!row.quoteItemId);

  if (creates.length > 0) {
    await allOrThrow(
      orpcClient.rfq.quoteItem.create(
        creates.map((row) => ({
          rfqQuoteId: vendorId,
          rfqItemId: row.rfqItemId,
          isAvailable: row.isAvailable,
          unitPrice: row.unitPrice,
          includesFreight: row.includesFreight,
          includesTax: row.includesTax,
          deliveryDate: row.deliveryDate ? new Date(row.deliveryDate) : null,
          remark: row.remark || null,
        })) as any,
      ),
    );
  }

  if (updates.length > 0) {
    await allOrThrow(
      orpcClient.rfq.quoteItem.update(
        updates.map((row) => ({
          id: row.quoteItemId as string,
          isAvailable: row.isAvailable,
          unitPrice: row.unitPrice,
          includesFreight: row.includesFreight,
          includesTax: row.includesTax,
          deliveryDate: row.deliveryDate ? new Date(row.deliveryDate) : null,
          remark: row.remark || null,
        })) as any,
      ),
    );
  }
}

export async function saveRfqAwards(awards: RfqAward[]): Promise<void> {
  if (awards.length === 0) return;
  await allOrThrow(
    orpcClient.rfq.award.update(
      awards.map((award) => ({
        rfqItemId: award.lineId,
        awardedQuoteId: award.skip ? null : (award.vendorId ?? null),
        notPurchased: award.skip,
      })) as any,
    ),
  );
}

export async function submitRfqForReview(id: string): Promise<void> {
  await single(orpcClient.rfq.submitForReview([{ id }]));
}

export async function approveRfq(id: string): Promise<void> {
  await single(orpcClient.rfq.approve([{ id }]));
}

export async function rejectRfq(
  id: string,
  rejectedReason: string,
): Promise<void> {
  await single(orpcClient.rfq.reject([{ id, rejectedReason }]));
}

export async function uploadRfqAttachment(file: File): Promise<RfqAttachment> {
  const sas = await orpcClient.storage.createBlobSasUrl({
    fileName: file.name,
    contentType: file.type,
  });
  const response = await fetch(sas.blobSasUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type,
    },
    body: file,
  });
  if (!response.ok) throw new Error(response.statusText || "upload failed");
  return {
    id: sas.blobUrl,
    name: file.name,
    kind: "other",
    size:
      file.size > 1024 * 1024
        ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
        : `${Math.max(1, Math.round(file.size / 1024))} KB`,
    uploadedBy: "",
    uploadedAt: toDateInput(new Date()),
  };
}

export async function loadPurchaseOrderSources(): Promise<
  {
    id: string;
    name: string;
    partnerId: string;
    purchaseDate: string;
    status: string;
  }[]
> {
  const rows = (await orpcClient.purchaseOrder.list({})) as unknown as {
    id: string;
    name: string;
    partnerId: string;
    purchaseDate: Date | string | null;
    status: string;
  }[];
  return (rows ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    partnerId: row.partnerId,
    purchaseDate: toDateInput(row.purchaseDate),
    status: row.status,
  }));
}

export async function loadPurchaseOrderLines(id: string): Promise<
  { id: string; productId: string; amount: number; unitPrice: number | null }[]
> {
  const detail = (await single(
    orpcClient.purchaseOrder.read([{ id }]),
  )) as unknown as {
    purchaseOrderProduct?: {
      id: string;
      productId: string;
      amount: number;
      unitPrice: number | null;
    }[];
  };
  return detail?.purchaseOrderProduct ?? [];
}
